/**
 * Laser Ornament Studio - 3D WebGL Three.js Engine
 * File: assets/laser-studio-3d.js
 * Synthesizes 3D Scene Graph, Procedural Wood Shaders, Multi-Layer Extrusion, Accessories, Interactive Viewer & Thumbnail Snapshotter.
 */

window.LaserStudio = window.LaserStudio || {};


/* ==========================================================================
   3D MODULE: laser-studio-3d-scene.js
   ========================================================================== */

/**
 * Laser Ornament Studio - 3D WebGL Scene & Lighting Engine
 * Module: assets/laser-studio-3d-scene.js
 * Responsibility: Manages WebGL renderer, OrbitControls, physical lighting rigs, shadow planes, and GPU resource disposal.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserStudio.Wood3DScene = (function () {
  'use strict';

  // Studio Lighting Presets
  const LIGHTING_PRESETS = {
    warm_studio: {
      name: 'Warm Photography Studio',
      ambientColor: 0xfff6ea,
      ambientIntensity: 0.75,
      keyColor: 0xfff4e6,
      keyIntensity: 1.4,
      keyPos: [150, 220, 200],
      rimColor: 0xffd28a,
      rimIntensity: 0.9,
      rimPos: [-160, -180, -140],
      bgColor: 0x090b14
    },
    workshop: {
      name: 'Laser Workshop Bench',
      ambientColor: 0xf0f4ff,
      ambientIntensity: 0.8,
      keyColor: 0xffffff,
      keyIntensity: 1.5,
      keyPos: [0, 260, 180],
      rimColor: 0x38bdf8,
      rimIntensity: 0.85,
      rimPos: [180, -140, -120],
      bgColor: 0x070b16
    },
    golden_hour: {
      name: 'Golden Sunset Glow',
      ambientColor: 0xfef3c7,
      ambientIntensity: 0.65,
      keyColor: 0xfbbf24,
      keyIntensity: 1.8,
      keyPos: [200, 100, 160],
      rimColor: 0xffedd5,
      rimIntensity: 1.15,
      rimPos: [-180, -180, -140],
      bgColor: 0x120a16
    },
    daylight: {
      name: 'Clean Neutral Daylight',
      ambientColor: 0xffffff,
      ambientIntensity: 0.9,
      keyColor: 0xffffff,
      keyIntensity: 1.3,
      keyPos: [120, 200, 220],
      rimColor: 0xdbeafe,
      rimIntensity: 0.7,
      rimPos: [-150, -150, -120],
      bgColor: 0x0b0f19
    }
  };

  /**
   * Initializes WebGL scene, camera, renderer, lighting, and shadow floor
   */
  function initScene(container, options = {}) {
    if (!container || typeof THREE === 'undefined') return null;

    const width = container.clientWidth || 800;
    const height = container.clientHeight || 600;

    // 1. Scene
    const scene = new THREE.Scene();
    const preset = LIGHTING_PRESETS[options.lighting] || LIGHTING_PRESETS.warm_studio;
    scene.background = new THREE.Color(preset.bgColor);

    // 2. Camera (38° FOV for minimal perspective distortion)
    const camera = new THREE.PerspectiveCamera(38, width / height, 1, 3000);
    camera.position.set(0, 0, 320);

    // 3. WebGL Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance'
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;
    if (THREE.sRGBEncoding) renderer.outputEncoding = THREE.sRGBEncoding;

    container.innerHTML = '';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.outline = 'none';
    renderer.domElement.style.cursor = 'grab';
    container.appendChild(renderer.domElement);

    // 4. OrbitControls
    let controls = null;
    if (THREE.OrbitControls) {
      controls = new THREE.OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.075;
      controls.rotateSpeed = 0.85;
      controls.zoomSpeed = 1.1;
      controls.panSpeed = 0.8;
      controls.minDistance = 60;
      controls.maxDistance = 1200;
      controls.autoRotate = !!options.autoRotate;
      controls.autoRotateSpeed = options.autoRotateSpeed || 1.2;
      controls.target.set(0, 0, 0);

      renderer.domElement.addEventListener('pointerdown', () => {
        renderer.domElement.style.cursor = 'grabbing';
      });
      window.addEventListener('pointerup', () => {
        renderer.domElement.style.cursor = 'grab';
      });
    }

    // 5. Lighting Rig
    const lights = setupLights(scene, options.lighting);

    // 6. Floor & Drop Shadow
    const floor = setupFloor(scene);

    // 7. Group container for 3D meshes
    const meshGroup = new THREE.Group();
    scene.add(meshGroup);

    return {
      scene,
      camera,
      renderer,
      controls,
      lights,
      floor,
      meshGroup
    };
  }

  /**
   * Sets up balanced physical studio lights
   */
  function setupLights(scene, lightingKey) {
    const preset = LIGHTING_PRESETS[lightingKey] || LIGHTING_PRESETS.warm_studio;

    const ambientLight = new THREE.AmbientLight(preset.ambientColor, preset.ambientIntensity);
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(preset.keyColor, preset.keyIntensity);
    keyLight.position.set(...preset.keyPos);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = 2048;
    keyLight.shadow.mapSize.height = 2048;
    keyLight.shadow.camera.near = 10;
    keyLight.shadow.camera.far = 800;
    const d = 160;
    keyLight.shadow.camera.left = -d;
    keyLight.shadow.camera.right = d;
    keyLight.shadow.camera.top = d;
    keyLight.shadow.camera.bottom = -d;
    keyLight.shadow.bias = -0.0001;
    keyLight.shadow.normalBias = 0.04; // Eliminates shadow acne on bevel edges
    keyLight.shadow.radius = 3.5;
    scene.add(keyLight);

    const rimLight = new THREE.DirectionalLight(preset.rimColor, preset.rimIntensity);
    rimLight.position.set(...preset.rimPos);
    scene.add(rimLight);

    return { ambientLight, keyLight, rimLight };
  }

  /**
   * Updates existing lights to a new preset
   */
  function updateLighting(scene, lights, lightingKey) {
    const preset = LIGHTING_PRESETS[lightingKey] || LIGHTING_PRESETS.warm_studio;
    if (scene) scene.background = new THREE.Color(preset.bgColor);

    if (lights.ambientLight) {
      lights.ambientLight.color.setHex(preset.ambientColor);
      lights.ambientLight.intensity = preset.ambientIntensity;
    }
    if (lights.keyLight) {
      lights.keyLight.color.setHex(preset.keyColor);
      lights.keyLight.intensity = preset.keyIntensity;
      lights.keyLight.position.set(...preset.keyPos);
    }
    if (lights.rimLight) {
      lights.rimLight.color.setHex(preset.rimColor);
      lights.rimLight.intensity = preset.rimIntensity;
      lights.rimLight.position.set(...preset.rimPos);
    }
  }

  /**
   * Builds shadow receiver plane and subtle dark backdrop
   */
  function setupFloor(scene) {
    const planeGeo = new THREE.PlaneGeometry(1000, 1000);
    const shadowMat = new THREE.ShadowMaterial({ opacity: 0.35 });
    const shadowPlane = new THREE.Mesh(planeGeo, shadowMat);
    shadowPlane.position.z = -12;
    shadowPlane.receiveShadow = true;
    scene.add(shadowPlane);

    const backdropGeo = new THREE.PlaneGeometry(1400, 1400);
    const backdropMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.15
    });
    const backdrop = new THREE.Mesh(backdropGeo, backdropMat);
    backdrop.position.z = -14;
    scene.add(backdrop);

    return { shadowPlane, backdrop };
  }

  /**
   * Recursively disposes geometries, materials, and attached WebGL textures
   */
  function disposeObject(obj) {
    if (!obj) return;
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
      materials.forEach((mat) => {
        if (mat) {
          if (mat.map && typeof mat.map.dispose === 'function') mat.map.dispose();
          if (mat.bumpMap && typeof mat.bumpMap.dispose === 'function') mat.bumpMap.dispose();
          if (mat.roughnessMap && typeof mat.roughnessMap.dispose === 'function') mat.roughnessMap.dispose();
          if (mat.metalnessMap && typeof mat.metalnessMap.dispose === 'function') mat.metalnessMap.dispose();
          if (mat.normalMap && typeof mat.normalMap.dispose === 'function') mat.normalMap.dispose();
          if (mat.alphaMap && typeof mat.alphaMap.dispose === 'function') mat.alphaMap.dispose();
          mat.dispose();
        }
      });
    }
    if (obj.children && obj.children.length > 0) {
      while (obj.children.length > 0) {
        const child = obj.children[0];
        disposeObject(child);
        obj.remove(child);
      }
    }
  }

  return {
    LIGHTING_PRESETS,
    initScene,
    setupLights,
    updateLighting,
    setupFloor,
    disposeObject
  };
})();


/* ==========================================================================
   3D MODULE: laser-studio-3d-materials.js
   ========================================================================== */

/**
 * Laser Ornament Studio - 3D Procedural Wood & Acrylic Materials Engine
 * Module: assets/laser-studio-3d-materials.js
 * Responsibility: Generates high-resolution procedural wood grain, charred laser cut edges, scorch gradients, and acrylic shaders.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserStudio.Wood3DMaterials = (function () {
  'use strict';

  // Wood Species Matrix with Physical Optics Properties
  const WOOD_SPECIES = {
    birch: {
      name: 'Baltic Birch Plywood',
      subtitle: 'Warm honey blonde, fine grain, layered edge',
      baseColor: '#e6cfb3',
      grainColor: '#c69d72',
      grainAltColor: '#dcb892',
      poreColor: '#b88d62',
      edgeBase: '#3d1e0a',
      edgeChar: '#1f0c03',
      roughness: 0.42,
      metalness: 0.03,
      isPlywood: true
    },
    walnut: {
      name: 'American Walnut',
      subtitle: 'Deep rich chocolate espresso, satin grain',
      baseColor: '#442a1b',
      grainColor: '#28150c',
      grainAltColor: '#5c3c2a',
      poreColor: '#201008',
      edgeBase: '#1a0d06',
      edgeChar: '#0e0603',
      roughness: 0.42,
      metalness: 0.06,
      isPlywood: false
    },
    oak: {
      name: 'Natural White Oak',
      subtitle: 'Warm golden amber, prominent medullary rays',
      baseColor: '#d6ad7a',
      grainColor: '#9d713c',
      grainAltColor: '#c59a65',
      poreColor: '#845826',
      edgeBase: '#3c210d',
      edgeChar: '#221106',
      roughness: 0.58,
      metalness: 0.04,
      isPlywood: false
    },
    cherry: {
      name: 'Warm Cherry Wood',
      subtitle: 'Reddish auburn tones, smooth glowing grain',
      baseColor: '#ba6643',
      grainColor: '#7b3117',
      grainAltColor: '#d17d58',
      poreColor: '#65230d',
      edgeBase: '#351207',
      edgeChar: '#1d0803',
      roughness: 0.38,
      metalness: 0.05,
      isPlywood: false
    },
    maple: {
      name: 'Hard Maple',
      subtitle: 'Clean ivory cream, subtle delicate grain',
      baseColor: '#f7ebda',
      grainColor: '#dec7ac',
      grainAltColor: '#efe1ce',
      poreColor: '#ceb494',
      edgeBase: '#58361b',
      edgeChar: '#321c0b',
      roughness: 0.34,
      metalness: 0.02,
      isPlywood: false
    },
    pine: {
      name: 'Rustic Pine',
      subtitle: 'Warm honey pine with organic growth rings',
      baseColor: '#e8cca0',
      grainColor: '#b88949',
      grainAltColor: '#dbc092',
      poreColor: '#9e6d30',
      edgeBase: '#43250e',
      edgeChar: '#251306',
      roughness: 0.64,
      metalness: 0.03,
      isPlywood: false
    },
    ebony: {
      name: 'Black Stained / Ebony',
      subtitle: 'Modern matte charcoal luxury wood',
      baseColor: '#282624',
      grainColor: '#141312',
      grainAltColor: '#3c3937',
      poreColor: '#0e0d0c',
      edgeBase: '#0d0c0c',
      edgeChar: '#050505',
      roughness: 0.48,
      metalness: 0.08,
      isPlywood: false
    },
    mdf: {
      name: 'Raw Kraft MDF',
      subtitle: 'Smooth engineered wood fiberboard',
      baseColor: '#b89d79',
      grainColor: '#a28662',
      grainAltColor: '#c4ab89',
      poreColor: '#8c714d',
      edgeBase: '#3b2817',
      edgeChar: '#22150a',
      roughness: 0.85,
      metalness: 0.01,
      isPlywood: false
    }
  };

  // Layer 2 Acrylic Infill Swatches with Specular Response Tokens
  const FILLER_COLORS = {
    red: {
      id: 'red',
      name: 'Crimson Holiday Red',
      hex: '#dc2626',
      material: 'acrylic_gloss',
      roughness: 0.2,
      metalness: 0.04,
      bgGradient: 'linear-gradient(135deg, #ef4444, #991b1b)'
    },
    green: {
      id: 'green',
      name: 'Emerald Pine Green',
      hex: '#059669',
      material: 'acrylic_gloss',
      roughness: 0.22,
      metalness: 0.04,
      bgGradient: 'linear-gradient(135deg, #10b981, #064e3b)'
    },
    blue: {
      id: 'blue',
      name: 'Royal Sapphire Blue',
      hex: '#1d4ed8',
      material: 'acrylic_gloss',
      roughness: 0.2,
      metalness: 0.05,
      bgGradient: 'linear-gradient(135deg, #3b82f6, #1e3a8a)'
    },
    volt: {
      id: 'volt',
      name: 'Ride Volt Yellow',
      hex: '#e3fc02',
      material: 'acrylic_gloss',
      roughness: 0.25,
      metalness: 0.02,
      bgGradient: 'linear-gradient(135deg, #facc15, #e3fc02)'
    },
    pink: {
      id: 'pink',
      name: 'Pastel Blush Pink',
      hex: '#f472b6',
      material: 'acrylic_satin',
      roughness: 0.35,
      metalness: 0.02,
      bgGradient: 'linear-gradient(135deg, #fbcfe8, #db2777)'
    },
    purple: {
      id: 'purple',
      name: 'Velvet Lavender Purple',
      hex: '#8b5cf6',
      material: 'acrylic_satin',
      roughness: 0.32,
      metalness: 0.03,
      bgGradient: 'linear-gradient(135deg, #c084fc, #6b21a8)'
    }
  };

  /**
   * Generates procedural wood grain texture maps with 3-tier laser scorch burns
   * @param {String} speciesKey - Key in WOOD_SPECIES ('birch', 'walnut', etc.)
   * @param {Array<String>} scorePathsSvg - Array of SVG path data strings for score lines
   * @param {Object} bounds - Bounding box of the cut shape { minX, minY, width, height }
   * @param {Object} options - Configuration options (charIntensity, scoreDepth)
   */
  function createWoodTextures(speciesKey, scorePathsSvg = [], bounds = null, options = {}) {
    if (typeof THREE === 'undefined') return null;

    const species = WOOD_SPECIES[speciesKey] || WOOD_SPECIES.birch;
    const texSize = 2048;

    // 1. Face Color Canvas
    const colorCanvas = document.createElement('canvas');
    colorCanvas.width = texSize;
    colorCanvas.height = texSize;
    const ctx = colorCanvas.getContext('2d');

    // 2. Bump Map Canvas (relief grooves)
    const bumpCanvas = document.createElement('canvas');
    bumpCanvas.width = texSize;
    bumpCanvas.height = texSize;
    const bCtx = bumpCanvas.getContext('2d');

    // 3. Roughness Map Canvas (laser char matte spots)
    const roughCanvas = document.createElement('canvas');
    roughCanvas.width = texSize;
    roughCanvas.height = texSize;
    const rCtx = roughCanvas.getContext('2d');

    // Base fill
    ctx.fillStyle = species.baseColor;
    ctx.fillRect(0, 0, texSize, texSize);

    bCtx.fillStyle = '#808080';
    bCtx.fillRect(0, 0, texSize, texSize);

    const baseRoughnessByte = Math.round(species.roughness * 255);
    rCtx.fillStyle = `rgb(${baseRoughnessByte}, ${baseRoughnessByte}, ${baseRoughnessByte})`;
    rCtx.fillRect(0, 0, texSize, texSize);

    // Procedural Growth Rings & Grain Waves
    const numRings = species.isPlywood ? 85 : 120;
    const ringSpacing = texSize / numRings;

    for (let i = 0; i < numRings; i++) {
      const yPos = i * ringSpacing;
      const waveFreq = 0.003 + (i % 5) * 0.001;
      const waveAmp = 18 + (i % 7) * 4;

      ctx.beginPath();
      ctx.moveTo(0, yPos);
      for (let x = 0; x <= texSize; x += 16) {
        const wave = Math.sin(x * waveFreq) * waveAmp + Math.sin(x * waveFreq * 3.1) * (waveAmp * 0.25);
        ctx.lineTo(x, yPos + wave);
      }

      const isAlt = i % 3 === 0;
      const grainAlpha = (i % 2 === 0 ? 0.22 : 0.12) + Math.sin(i * 0.4) * 0.06;
      ctx.strokeStyle = isAlt ? species.grainAltColor : species.grainColor;
      ctx.globalAlpha = Math.max(0.05, grainAlpha);
      ctx.lineWidth = 1.5 + (i % 4) * 1.2;
      ctx.stroke();

      bCtx.beginPath();
      bCtx.moveTo(0, yPos);
      for (let x = 0; x <= texSize; x += 16) {
        const wave = Math.sin(x * waveFreq) * waveAmp + Math.sin(x * waveFreq * 3.1) * (waveAmp * 0.25);
        bCtx.lineTo(x, yPos + wave);
      }
      bCtx.strokeStyle = isAlt ? '#909090' : '#707070';
      bCtx.globalAlpha = 0.25;
      bCtx.lineWidth = 2.0;
      bCtx.stroke();
    }
    ctx.globalAlpha = 1.0;
    bCtx.globalAlpha = 1.0;

    // Longitudinal Wood Pores & Fibers
    const numFibers = 600;
    for (let f = 0; f < numFibers; f++) {
      const fx = Math.random() * texSize;
      const fy = Math.random() * texSize;
      const fLen = 30 + Math.random() * 120;
      const fThick = 0.6 + Math.random() * 1.4;

      ctx.fillStyle = f % 4 === 0 ? species.poreColor : species.grainColor;
      ctx.globalAlpha = 0.08 + Math.random() * 0.14;
      ctx.fillRect(fx, fy, fLen, fThick);

      bCtx.fillStyle = '#606060';
      bCtx.globalAlpha = 0.12;
      bCtx.fillRect(fx, fy, fLen, fThick * 1.2);
    }
    ctx.globalAlpha = 1.0;
    bCtx.globalAlpha = 1.0;

    // Laser Score Scorching onto Wood Face
    if (scorePathsSvg && scorePathsSvg.length > 0 && bounds) {
      const scaleX = texSize / (bounds.width || 1);
      const scaleY = texSize / (bounds.height || 1);

      let engraveCoreWidth = 2.0;

      if (options.scoreDepth === 'light') {
        engraveCoreWidth = 1.4;
      } else if (options.scoreDepth === 'deep') {
        engraveCoreWidth = 2.6;
      }

      scorePathsSvg.forEach((pathData) => {
        if (!pathData) return;
        try {
          const path2D = new Path2D(pathData);

          ctx.save();
          bCtx.save();
          rCtx.save();

          ctx.translate(-bounds.minX * scaleX, -bounds.minY * scaleY);
          ctx.scale(scaleX, scaleY);

          bCtx.translate(-bounds.minX * scaleX, -bounds.minY * scaleY);
          bCtx.scale(scaleX, scaleY);

          rCtx.translate(-bounds.minX * scaleX, -bounds.minY * scaleY);
          rCtx.scale(scaleX, scaleY);

          // 1. Subtle Toasted Caramel Heat Margin (narrow, authentic heat rim)
          ctx.strokeStyle = 'rgba(95, 38, 12, 0.45)';
          ctx.lineWidth = engraveCoreWidth * 1.8;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke(path2D);

          // 2. Charred Laser Kerf Wall
          ctx.strokeStyle = 'rgba(32, 12, 5, 0.88)';
          ctx.lineWidth = engraveCoreWidth * 1.3;
          ctx.stroke(path2D);

          // 3. Deep Carbon Black Core
          ctx.strokeStyle = 'rgba(10, 4, 2, 0.98)';
          ctx.lineWidth = engraveCoreWidth * 0.95;
          ctx.stroke(path2D);

          // 4. Micro Channel Shadow (Carved Ambient Occlusion)
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.92)';
          ctx.lineWidth = engraveCoreWidth * 0.65;
          ctx.stroke(path2D);

          // Realistic V-Groove Bump Profile (Smooth Stepped Relief from Height 128 to 0)
          // Outer beveled lip:
          bCtx.strokeStyle = 'rgb(105, 105, 105)';
          bCtx.lineWidth = engraveCoreWidth * 2.2;
          bCtx.lineCap = 'round';
          bCtx.lineJoin = 'round';
          bCtx.stroke(path2D);

          // Mid-slope wall:
          bCtx.strokeStyle = 'rgb(60, 60, 60)';
          bCtx.lineWidth = engraveCoreWidth * 1.5;
          bCtx.stroke(path2D);

          // Deep inner groove channel:
          bCtx.strokeStyle = 'rgb(0, 0, 0)';
          bCtx.lineWidth = engraveCoreWidth * 0.9;
          bCtx.stroke(path2D);

          // Roughness Map (Matte Carbon Char - High Roughness 248 absorbs light)
          rCtx.strokeStyle = 'rgb(248, 248, 248)';
          rCtx.lineWidth = engraveCoreWidth * 1.6;
          rCtx.lineCap = 'round';
          rCtx.lineJoin = 'round';
          rCtx.stroke(path2D);

          ctx.restore();
          bCtx.restore();
          rCtx.restore();
        } catch (err) {
          console.warn('[LaserStudio.Wood3DMaterials] Error rendering score path to canvas:', err);
        }
      });
    }

    const faceColorTex = new THREE.CanvasTexture(colorCanvas);
    faceColorTex.wrapS = THREE.ClampToEdgeWrapping;
    faceColorTex.wrapT = THREE.ClampToEdgeWrapping;
    if (THREE.sRGBEncoding) faceColorTex.encoding = THREE.sRGBEncoding;

    const faceBumpTex = new THREE.CanvasTexture(bumpCanvas);
    faceBumpTex.wrapS = THREE.ClampToEdgeWrapping;
    faceBumpTex.wrapT = THREE.ClampToEdgeWrapping;

    const faceRoughTex = new THREE.CanvasTexture(roughCanvas);
    faceRoughTex.wrapS = THREE.ClampToEdgeWrapping;
    faceRoughTex.wrapT = THREE.ClampToEdgeWrapping;

    // Side Wall Charred Laser Cut Texture (512x512)
    const edgeCanvas = document.createElement('canvas');
    edgeCanvas.width = 512;
    edgeCanvas.height = 512;
    const eCtx = edgeCanvas.getContext('2d');

    let edgeCharColor = species.edgeChar;
    let edgeBaseColor = species.edgeBase;
    if (options.charIntensity === 'light') {
      edgeCharColor = species.edgeBase;
      edgeBaseColor = species.baseColor;
    } else if (options.charIntensity === 'heavy') {
      edgeCharColor = '#060302';
      edgeBaseColor = '#180c05';
    }

    const edgeGrad = eCtx.createLinearGradient(0, 0, 0, 512);
    edgeGrad.addColorStop(0, edgeCharColor);
    edgeGrad.addColorStop(0.5, edgeBaseColor);
    edgeGrad.addColorStop(1, edgeCharColor);
    eCtx.fillStyle = edgeGrad;
    eCtx.fillRect(0, 0, 512, 512);

    for (let x = 0; x < 512; x += 3) {
      eCtx.fillStyle = x % 6 === 0 ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 140, 50, 0.08)';
      eCtx.fillRect(x, 0, 1.5, 512);
    }

    if (species.isPlywood) {
      const numPlies = 5;
      for (let p = 1; p < numPlies; p++) {
        const plyY = (p / numPlies) * 512;
        eCtx.fillStyle = 'rgba(0, 0, 0, 0.35)';
        eCtx.fillRect(0, plyY - 3, 512, 6);
      }
    }

    const edgeTex = new THREE.CanvasTexture(edgeCanvas);
    edgeTex.wrapS = THREE.RepeatWrapping;
    edgeTex.wrapT = THREE.RepeatWrapping;
    if (THREE.sRGBEncoding) edgeTex.encoding = THREE.sRGBEncoding;

    return {
      faceMap: faceColorTex,
      faceBump: faceBumpTex,
      faceRoughness: faceRoughTex,
      edgeMap: edgeTex,
      species
    };
  }

  return {
    WOOD_SPECIES,
    FILLER_COLORS,
    createWoodTextures
  };
})();


/* ==========================================================================
   3D MODULE: laser-studio-3d-extrusion.js
   ========================================================================== */

/**
 * Laser Ornament Studio - 3D Multi-Layer Extrusion Engine
 * Module: assets/laser-studio-3d-extrusion.js
 * Responsibility: Parses SVG vectors, extrudes Layer 1 (Wood with Bevels) and Layer 2 (Acrylic Infill Backplate), and detects apex hanging loop holes.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserStudio.Wood3DExtrusion = (function () {
  'use strict';

  /**
   * Parses SVG string and separates into Cut Shapes and Score Path Data strings
   */
  function parseSVGPaths(svgString) {
    if (!svgString || typeof THREE === 'undefined' || !THREE.SVGLoader) return null;

    const loader = new THREE.SVGLoader();
    const svgData = loader.parse(svgString);
    if (!svgData || !svgData.paths || svgData.paths.length === 0) return null;

    const cutShapes = [];
    const scorePathDatas = [];
    const rawCutShapes = [];

    svgData.paths.forEach((path) => {
      const node = path.userData ? path.userData.node : null;
      const stroke = (node ? node.getAttribute('stroke') : '') || (path.color ? path.color.getHexString() : '');
      const strokeUpper = stroke.toUpperCase();
      const d = node ? node.getAttribute('d') : null;

      const isCut =
        strokeUpper.includes('E11D48') ||
        strokeUpper.includes('FF3B30') ||
        strokeUpper.includes('FF0000') ||
        strokeUpper.includes('225, 29, 72') ||
        strokeUpper.includes('225,29,72') ||
        strokeUpper.includes('255, 59, 48') ||
        strokeUpper.includes('255, 0, 0') ||
        (node && node.id && node.id.startsWith('cut-')) ||
        (node && node.getAttribute('data-name') && node.getAttribute('data-name').startsWith('cut-')) ||
        (node && node.getAttribute('name') && node.getAttribute('name').startsWith('cut-'));

      if (isCut) {
        const shapes = THREE.SVGLoader.createShapes(path);
        if (shapes && shapes.length > 0) rawCutShapes.push(...shapes);
      } else {
        if (d) scorePathDatas.push(d);
      }
    });

    if (rawCutShapes.length === 0) {
      svgData.paths.forEach((path) => {
        const shapes = THREE.SVGLoader.createShapes(path);
        if (shapes && shapes.length > 0) rawCutShapes.push(...shapes);
      });
    }

    cutShapes.push(...rawCutShapes);

    return {
      cutShapes,
      scorePathDatas
    };
  }

  /**
   * Computes bounding box across shapes
   */
  function calculateBounds(shapes) {
    if (!shapes || shapes.length === 0) {
      return { minX: 0, minY: 0, maxX: 100, maxY: 100, width: 100, height: 100, cx: 50, cy: 50 };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    shapes.forEach((shape) => {
      const pts = shape.getPoints();
      pts.forEach((p) => {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      });
    });

    const width = maxX - minX || 100;
    const height = maxY - minY || 100;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    return { minX, minY, maxX, maxY, width, height, cx, cy };
  }

  /**
   * Scans shape holes to detect the top hanging loop hole strictly at the apex
   */
  function detectHangingLoopApex(shapes, bounds) {
    if (!shapes || !bounds) return null;

    let detectedHole = null;

    shapes.forEach((shape) => {
      if (shape.holes && shape.holes.length > 0) {
        shape.holes.forEach((hole) => {
          const holePts = hole.getPoints();
          if (holePts.length > 0) {
            let hMinX = Infinity, hMaxX = -Infinity, hMinY = Infinity, hMaxY = -Infinity;
            let hSumX = 0, hSumY = 0;

            holePts.forEach((hp) => {
              hMinX = Math.min(hMinX, hp.x);
              hMaxX = Math.max(hMaxX, hp.x);
              hMinY = Math.min(hMinY, hp.y);
              hMaxY = Math.max(hMaxY, hp.y);
              hSumX += hp.x;
              hSumY += hp.y;
            });

            const hAvgX = hSumX / holePts.length;
            const hAvgY = hSumY / holePts.length;
            const hW = hMaxX - hMinX;
            const hH = hMaxY - hMinY;

            // Strict hanging apex criteria (top 11% height, centered horizontally, small and roughly round)
            const isApexTop = hAvgY < (bounds.minY + bounds.height * 0.11);
            const isNearCenter = Math.abs(hAvgX - bounds.cx) < (bounds.width * 0.18);
            const isSmallHole = hW < (bounds.width * 0.09) && hH < (bounds.height * 0.09);
            const isRound = (hW > 0 && hH > 0) && (Math.min(hW, hH) / Math.max(hW, hH) > 0.55);

            if (isApexTop && isNearCenter && isSmallHole && isRound) {
              if (!detectedHole || hAvgY < detectedHole.y) {
                detectedHole = { x: hAvgX, y: hAvgY, radius: Math.max(hW, hH) / 2 };
              }
            }
          }
        });
      }
    });

    return detectedHole;
  }

  /**
   * Extrudes Layer 1 (Front Wood Mesh) with beveled edges and UV mappings
   */
  function extrudeWoodLayer(cutShapes, bounds, depth, totalDepth) {
    const customUVGenerator = {
      generateTopUV: function (geometry, vertices, indexA, indexB, indexC) {
        const ax = vertices[indexA * 3], ay = vertices[indexA * 3 + 1];
        const bx = vertices[indexB * 3], by = vertices[indexB * 3 + 1];
        const cx = vertices[indexC * 3], cy = vertices[indexC * 3 + 1];
        return [
          new THREE.Vector2((ax - bounds.minX) / bounds.width, 1.0 - (ay - bounds.minY) / bounds.height),
          new THREE.Vector2((bx - bounds.minX) / bounds.width, 1.0 - (by - bounds.minY) / bounds.height),
          new THREE.Vector2((cx - bounds.minX) / bounds.width, 1.0 - (cy - bounds.minY) / bounds.height)
        ];
      },
      generateSideWallUV: function () {
        return [
          new THREE.Vector2(0, 0),
          new THREE.Vector2(1, 0),
          new THREE.Vector2(1, 1),
          new THREE.Vector2(0, 1)
        ];
      }
    };

    const extrudeSettings = {
      depth: depth,
      bevelEnabled: true,
      bevelSegments: 2,
      steps: 1,
      bevelSize: 0.35,
      bevelThickness: 0.35,
      UVGenerator: customUVGenerator
    };

    const geometry = new THREE.ExtrudeGeometry(cutShapes, extrudeSettings);
    geometry.translate(-bounds.cx, -bounds.cy, -totalDepth / 2);
    geometry.computeVertexNormals();

    return geometry;
  }

  /**
   * Extrudes Layer 2 (Acrylic Infill Backplate) with internal hole elimination
   */
  function extrudeInfillLayer(cutShapes, bounds, depth, depthOffset, totalDepth) {
    const layer2Shapes = [];

    cutShapes.forEach((shape) => {
      const l2Shape = new THREE.Shape();
      l2Shape.curves = shape.curves.slice();

      if (shape.holes && shape.holes.length > 0) {
        shape.holes.forEach((hole) => {
          const hPts = hole.getPoints();
          if (hPts.length > 0) {
            let hMinX = Infinity, hMaxX = -Infinity, hMinY = Infinity, hMaxY = -Infinity;
            let hSumX = 0, hSumY = 0;

            hPts.forEach((p) => {
              hMinX = Math.min(hMinX, p.x);
              hMaxX = Math.max(hMaxX, p.x);
              hMinY = Math.min(hMinY, p.y);
              hMaxY = Math.max(hMaxY, p.y);
              hSumX += p.x;
              hSumY += p.y;
            });

            const hAvgX = hSumX / hPts.length;
            const hAvgY = hSumY / hPts.length;
            const hW = hMaxX - hMinX;
            const hH = hMaxY - hMinY;

            // Retain apex hanging hole only
            const isApexTop = hAvgY < (bounds.minY + bounds.height * 0.11);
            const isNearCenter = Math.abs(hAvgX - bounds.cx) < (bounds.width * 0.18);
            const isTinyHole = hW < (bounds.width * 0.09) && hH < (bounds.height * 0.09);
            const isRound = (hW > 0 && hH > 0) && (Math.min(hW, hH) / Math.max(hW, hH) > 0.55);

            if (isApexTop && isNearCenter && isTinyHole && isRound) {
              l2Shape.holes.push(hole);
            }
          }
        });
      }
      layer2Shapes.push(l2Shape);
    });

    const extrudeSettings = {
      depth: depth,
      bevelEnabled: true,
      bevelSegments: 2,
      steps: 1,
      bevelSize: 0.25,
      bevelThickness: 0.25
    };

    const geometry = new THREE.ExtrudeGeometry(layer2Shapes, extrudeSettings);
    geometry.translate(-bounds.cx, -bounds.cy, -totalDepth / 2 + depthOffset);
    geometry.computeVertexNormals();

    return geometry;
  }

  return {
    parseSVGPaths,
    calculateBounds,
    detectHangingLoopApex,
    extrudeWoodLayer,
    extrudeInfillLayer
  };
})();


/* ==========================================================================
   3D MODULE: laser-studio-3d-accessories.js
   ========================================================================== */

/**
 * Laser Ornament Studio - 3D Hanging Accessories Engine
 * Module: assets/laser-studio-3d-accessories.js
 * Responsibility: Generates CatmullRom spline hanging ribbons (red satin, metallic gold, shimmer silver) and organic jute twine meshes.
 */

window.LaserStudio = window.LaserStudio || {};

window.LaserStudio.Wood3DAccessories = (function () {
  'use strict';

  /**
   * Builds 3D hanging ribbon or twine loop through the detected top hole
   * @param {String} accType - 'none', 'ribbon_red', 'ribbon_gold', 'silver', 'twine'
   * @param {Object} topLoopHole - { x, y, radius } coordinates
   * @param {Object} bounds - { minX, minY, width, height, cx, cy }
   * @param {Number} extrudeDepth - Total model thickness
   * @returns {THREE.Mesh|null}
   */
  function buildAccessory(accType, topLoopHole, bounds, extrudeDepth) {
    if (!accType || accType === 'none' || !topLoopHole || !bounds || typeof THREE === 'undefined') {
      return null;
    }

    const holeLocalX = topLoopHole.x - bounds.cx;
    const holeLocalY = -(topLoopHole.y - bounds.cy);
    const loopTopY = holeLocalY + bounds.height * 0.45;

    const curvePoints = [
      new THREE.Vector3(holeLocalX - 3, loopTopY, -2),
      new THREE.Vector3(holeLocalX - 1.5, holeLocalY + 12, -4),
      new THREE.Vector3(holeLocalX, holeLocalY, -extrudeDepth / 2 - 1),
      new THREE.Vector3(holeLocalX, holeLocalY, extrudeDepth / 2 + 1),
      new THREE.Vector3(holeLocalX + 1.5, holeLocalY + 12, 4),
      new THREE.Vector3(holeLocalX + 3, loopTopY, 2),
      new THREE.Vector3(holeLocalX, loopTopY + 8, 0)
    ];

    const curve = new THREE.CatmullRomCurve3(curvePoints, true);
    const isTwine = accType === 'twine';
    const radius = isTwine ? 1.1 : 1.8;
    const tubeGeo = new THREE.TubeGeometry(curve, 64, radius, 8, true);

    let tubeMat;
    if (accType === 'ribbon_red') {
      tubeMat = new THREE.MeshStandardMaterial({
        color: 0xba1328,
        roughness: 0.45,
        metalness: 0.1
      });
    } else if (accType === 'ribbon_gold') {
      tubeMat = new THREE.MeshStandardMaterial({
        color: 0xdfb143,
        roughness: 0.28,
        metalness: 0.45
      });
    } else if (accType === 'silver') {
      tubeMat = new THREE.MeshStandardMaterial({
        color: 0xd8e0ea,
        roughness: 0.25,
        metalness: 0.8
      });
    } else {
      // Jute Twine
      tubeMat = new THREE.MeshStandardMaterial({
        color: 0xba9b68,
        roughness: 0.95,
        metalness: 0.02
      });
    }

    const accessoryMesh = new THREE.Mesh(tubeGeo, tubeMat);
    accessoryMesh.castShadow = true;

    return accessoryMesh;
  }

  return {
    buildAccessory
  };
})();


/* ==========================================================================
   3D MODULE: laser-studio-3d-viewer.js
   ========================================================================== */

/**
 * Laser Ornament Studio - Master 3D WebGL Wood Viewer Engine
 * Module: assets/laser-studio-3d-viewer.js
 * Responsibility: Master orchestrator synthesizing scene graph, procedural wood shaders, multi-layer extrusion, orbit controls, and live updates.
 */

window.LaserStudio = window.LaserStudio || {};

(function (window) {
  'use strict';

  const SceneEngine = window.LaserStudio.Wood3DScene;
  const MaterialsEngine = window.LaserStudio.Wood3DMaterials;
  const ExtrusionEngine = window.LaserStudio.Wood3DExtrusion;
  const AccessoriesEngine = window.LaserStudio.Wood3DAccessories;

  class Wood3DViewer {
    constructor(container, options = {}) {
      this.container = typeof container === 'string' ? document.querySelector(container) : container;
      if (!this.container) {
        console.error('[Wood3D] Container element not found');
        return;
      }

      this.options = Object.assign(
        {
          woodType: 'birch',
          thickness: 5.0,
          charIntensity: 'medium',
          scoreDepth: 'medium',
          accessory: 'none',
          lighting: 'warm_studio',
          autoRotate: false,
          autoRotateSpeed: 1.2,
          showDimensions: false,
          showShadow: true,
          layer2: Object.assign(
            {
              enabled: true,
              colorId: 'green',
              color: '#059669',
              name: 'Emerald Pine Green',
              material: 'acrylic_gloss',
              thickness: 3.0
            },
            options.layer2 || {}
          ),
          onReady: null
        },
        options
      );

      this.currentSVG = null;
      this.isAnimating = true;
      this.animationFrameId = null;
      this.woodMesh = null;
      this.layer2Mesh = null;
      this.accessoryMesh = null;
      this.dimensionGroup = null;

      // Camera transition state
      this.targetCameraPos = null;
      this.targetCameraTarget = null;
      this.lerpingCamera = false;
      this.hasLoadedInitial = false;

      this.svgBounds = { minX: 0, minY: 0, maxX: 100, maxY: 100, width: 100, height: 100, cx: 50, cy: 50 };
      this.topLoopHole = null;

      this.init();

      if (typeof this.options.onReady === 'function') {
        this.options.onReady(this);
      }
    }

    init() {
      const sceneBundle = SceneEngine.initScene(this.container, this.options);
      if (!sceneBundle) return;

      this.scene = sceneBundle.scene;
      this.camera = sceneBundle.camera;
      this.renderer = sceneBundle.renderer;
      this.controls = sceneBundle.controls;
      this.lights = sceneBundle.lights;
      this.floor = sceneBundle.floor;
      this.meshGroup = sceneBundle.meshGroup;

      this.setupResizeObserver();

      this.animate = this.animate.bind(this);
      this.animate();
    }

    setupResizeObserver() {
      this.handleResize = () => {
        if (!this.container || !this.renderer || !this.camera) return;
        const width = this.container.clientWidth || 800;
        const height = this.container.clientHeight || 600;
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
      };

      window.addEventListener('resize', this.handleResize);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.handleResize());
        this.resizeObserver.observe(this.container);
      }
    }

    /**
     * Loads SVG vector string, extracts cut/score paths, and creates 3D multi-layer model
     */
    loadSVG(svgString) {
      if (!svgString) return;
      this.currentSVG = svgString;

      // Deep clean previous geometries and textures
      while (this.meshGroup.children.length > 0) {
        const obj = this.meshGroup.children[0];
        SceneEngine.disposeObject(obj);
        this.meshGroup.remove(obj);
      }

      try {
        const parsed = ExtrusionEngine.parseSVGPaths(svgString);
        if (!parsed || parsed.cutShapes.length === 0) return;

        const bounds = ExtrusionEngine.calculateBounds(parsed.cutShapes);
        this.svgBounds = bounds;
        this.topLoopHole = ExtrusionEngine.detectHangingLoopApex(parsed.cutShapes, bounds);

        // Procedural Textures
        const woodTextures = MaterialsEngine.createWoodTextures(
          this.options.woodType,
          parsed.scorePathDatas,
          bounds,
          this.options
        );

        const depth1 = Math.max(1.0, this.options.thickness * 1.8);
        const isLayer2 = !!(this.options.layer2 && this.options.layer2.enabled);
        const depth2 = isLayer2 ? Math.max(1.0, (this.options.layer2.thickness || 3.0) * 1.8) : 0;
        const totalDepth = depth1 + depth2;

        // Layer 1: Front Wood Geometry
        const geometry1 = ExtrusionEngine.extrudeWoodLayer(parsed.cutShapes, bounds, depth1, totalDepth);
        const species = MaterialsEngine.WOOD_SPECIES[this.options.woodType] || MaterialsEngine.WOOD_SPECIES.birch;

        const faceMaterial = new THREE.MeshStandardMaterial({
          map: woodTextures.faceMap,
          bumpMap: woodTextures.faceBump,
          bumpScale: 1.35,
          roughnessMap: woodTextures.faceRoughness,
          roughness: species.roughness,
          metalness: species.metalness,
          side: THREE.FrontSide
        });

        const edgeMaterial = new THREE.MeshStandardMaterial({
          map: woodTextures.edgeMap,
          roughness: 0.85,
          metalness: 0.02,
          side: THREE.FrontSide
        });

        this.woodMesh = new THREE.Mesh(geometry1, [faceMaterial, edgeMaterial]);
        this.woodMesh.castShadow = true;
        this.woodMesh.receiveShadow = true;
        this.woodMesh.rotation.x = Math.PI;
        this.meshGroup.add(this.woodMesh);

        // Layer 2: Acrylic Infill Backplate
        if (isLayer2) {
          const geometry2 = ExtrusionEngine.extrudeInfillLayer(parsed.cutShapes, bounds, depth2, depth1, totalDepth);
          const fillerColorHex = (this.options.layer2 && this.options.layer2.color) ? this.options.layer2.color : '#FFFFFF';
          const fillerDef = MaterialsEngine.FILLER_COLORS[this.options.layer2?.colorId] || { roughness: 0.2, metalness: 0.04 };

          const layer2Material = new THREE.MeshStandardMaterial({
            color: new THREE.Color(fillerColorHex),
            roughness: fillerDef.roughness !== undefined ? fillerDef.roughness : 0.2,
            metalness: fillerDef.metalness !== undefined ? fillerDef.metalness : 0.04,
            side: THREE.FrontSide
          });

          this.layer2Mesh = new THREE.Mesh(geometry2, layer2Material);
          this.layer2Mesh.castShadow = true;
          this.layer2Mesh.receiveShadow = true;
          this.layer2Mesh.rotation.x = Math.PI;
          this.meshGroup.add(this.layer2Mesh);
        } else {
          this.layer2Mesh = null;
        }

        // Shadow Plane Depth Placement
        if (this.floor && this.floor.shadowPlane) {
          this.floor.shadowPlane.position.z = -totalDepth / 2 - 8;
        }
        if (this.floor && this.floor.backdrop) {
          this.floor.backdrop.position.z = -totalDepth / 2 - 10;
        }

        // Hanging Accessories
        if (this.accessoryMesh) {
          SceneEngine.disposeObject(this.accessoryMesh);
          this.meshGroup.remove(this.accessoryMesh);
          this.accessoryMesh = null;
        }

        if (this.options.accessory && this.options.accessory !== 'none' && this.topLoopHole) {
          this.accessoryMesh = AccessoriesEngine.buildAccessory(
            this.options.accessory,
            this.topLoopHole,
            bounds,
            totalDepth
          );
          if (this.accessoryMesh) this.meshGroup.add(this.accessoryMesh);
        }

        // Camera Auto-Framing
        const maxDim = Math.max(bounds.width, bounds.height);
        const targetDist = maxDim * 2.1;
        if (this.controls) {
          this.controls.minDistance = maxDim * 0.4;
          this.controls.maxDistance = maxDim * 6;
        }

        if (!this.hasLoadedInitial) {
          this.camera.position.set(0, 0, targetDist);
          if (this.controls) this.controls.target.set(0, 0, 0);
          this.hasLoadedInitial = true;
        }
        if (this.controls) this.controls.update();
      } catch (err) {
        console.error('[Wood3D] Error rendering 3D model:', err);
      }
    }

    /**
     * Smoothly transitions camera to a preset angle
     * @param {String} viewName - 'front', 'isometric', 'side', 'back', 'top'
     */
    /**
     * Smoothly transitions camera to a preset angle
     * @param {String} viewName - 'front', 'isometric', 'side', 'back', 'top'
     */
    setCameraView(viewName) {
      if (!this.camera || !this.controls) return;

      const bounds = this.svgBounds || { width: 100, height: 100 };
      const maxDim = Math.max(bounds.width || 100, bounds.height || 100);
      const dist = this.camera.position.distanceTo(this.controls.target) || (maxDim * 2.1) || 300;
      let targetPos = new THREE.Vector3();

      switch (viewName) {
        case 'front':
          targetPos.set(0, 0, dist);
          break;
        case 'isometric':
          targetPos.set(0.7 * dist, 0.45 * dist, 0.7 * dist);
          break;
        case 'side':
          targetPos.set(dist, 0, 10);
          break;
        case 'back':
          targetPos.set(0, 0, -dist);
          break;
        case 'top':
          targetPos.set(0, 0.95 * dist, 0.25 * dist);
          break;
        default:
          targetPos.set(0, 0, dist);
      }

      this.camera.up.set(0, 1, 0);
      this.targetCameraPos = targetPos;
      this.targetCameraTarget = new THREE.Vector3(0, 0, 0);
      this.lerpingCamera = true;
    }

    /**
     * Resets camera smoothly to default centered front view
     */
    resetCamera() {
      if (!this.camera || !this.controls) return;
      const bounds = this.svgBounds || { width: 100, height: 100 };
      const maxDim = Math.max(bounds.width || 100, bounds.height || 100);
      const targetDist = maxDim * 2.1 || 300;

      this.camera.up.set(0, 1, 0);
      this.targetCameraPos = new THREE.Vector3(0, 0, targetDist);
      this.targetCameraTarget = new THREE.Vector3(0, 0, 0);
      this.lerpingCamera = true;
    }

    /**
     * Zooms in camera viewport toward control target
     */
    zoomIn(factor = 0.8) {
      if (!this.camera || !this.controls) return;
      const offset = this.camera.position.clone().sub(this.controls.target);
      offset.multiplyScalar(factor);
      this.camera.position.copy(this.controls.target).add(offset);
      this.controls.update();
    }

    /**
     * Zooms out camera viewport away from control target
     */
    zoomOut(factor = 1.25) {
      if (!this.camera || !this.controls) return;
      const offset = this.camera.position.clone().sub(this.controls.target);
      offset.multiplyScalar(factor);
      this.camera.position.copy(this.controls.target).add(offset);
      this.controls.update();
    }

    /**
     * Captures a high-resolution square snapshot data URL of the 3D scene,
     * automatically zooming camera tight into the ornament bounding box with a subtle 3D perspective angle,
     * rendering with a transparent background, and cropping to a perfect 1:1 square.
     */
    captureSnapshot(width = 400, height = 400, format = 'image/webp', quality = 0.9) {
      if (!this.renderer || !this.scene || !this.camera) return null;

      // Save original camera state and scene background / floor visibility
      const origPos = this.camera.position.clone();
      const origTarget = this.controls ? this.controls.target.clone() : new THREE.Vector3(0, 0, 0);
      const origBg = this.scene.background;
      const origBackdropVis = this.floor?.backdrop ? this.floor.backdrop.visible : true;
      const origShadowVis = this.floor?.shadowPlane ? this.floor.shadowPlane.visible : true;

      // Save renderer clear color & alpha
      const origClearColor = new THREE.Color();
      this.renderer.getClearColor(origClearColor);
      const origClearAlpha = this.renderer.getClearAlpha();

      try {
        // Isolate ornament with pure transparent background
        this.scene.background = null;
        if (this.floor?.backdrop) this.floor.backdrop.visible = false;
        if (this.floor?.shadowPlane) this.floor.shadowPlane.visible = false;
        this.renderer.setClearColor(0x000000, 0);

        // Calculate bounding box strictly from the ornament meshGroup (ignoring floor, shadowPlane, backdrop)
        const box = new THREE.Box3();
        if (this.meshGroup && this.meshGroup.children.length > 0) {
          box.setFromObject(this.meshGroup);
        } else {
          // Fallback: exclude floor & backdrop planes
          this.scene.traverse((obj) => {
            if (
              obj.isMesh &&
              obj.visible &&
              obj !== this.floor?.shadowPlane &&
              obj !== this.floor?.backdrop
            ) {
              box.union(new THREE.Box3().setFromObject(obj));
            }
          });
        }

        if (!box.isEmpty()) {
          const center = new THREE.Vector3();
          const size = new THREE.Vector3();
          box.getCenter(center);
          box.getSize(size);

          // Get max dimension in the viewing plane
          const maxDim = Math.max(size.x || 100, size.y || 100);
          const fovRad = this.camera.fov * (Math.PI / 180);

          // Tight framing: ornament fills ~85-90% of the viewport (1.18 margin)
          const fitDist = (maxDim / 2) / Math.tan(fovRad / 2) * 1.18;

          // Subtle 3D perspective angle (~8° side, ~4.5° top) so wood thickness, side cut burns, and acrylic depth are clearly visible
          const angleX = 0.14;
          const angleY = 0.08;
          const camDist = fitDist * 1.04;

          this.camera.position.set(
            center.x + Math.sin(angleX) * camDist,
            center.y + Math.sin(angleY) * camDist,
            center.z + Math.cos(angleX) * Math.cos(angleY) * camDist
          );
          this.camera.lookAt(center);
          if (this.controls) this.controls.target.copy(center);
        }

        this.renderer.render(this.scene, this.camera);

        // Crop centered square from wide viewport so thumbnail is not distorted or letterboxed
        const sourceCanvas = this.renderer.domElement;
        const cropSize = Math.min(sourceCanvas.width, sourceCanvas.height);
        const cropX = (sourceCanvas.width - cropSize) / 2;
        const cropY = (sourceCanvas.height - cropSize) / 2;

        const offscreenCanvas = document.createElement('canvas');
        offscreenCanvas.width = width;
        offscreenCanvas.height = height;
        const ctx = offscreenCanvas.getContext('2d');

        if (ctx) {
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(
            sourceCanvas,
            cropX, cropY, cropSize, cropSize,
            0, 0, width, height
          );
          return offscreenCanvas.toDataURL(format, quality);
        }

        return sourceCanvas.toDataURL(format, quality);
      } finally {
        // Always restore scene background, floor visibility, clear color, and camera position
        this.scene.background = origBg;
        if (this.floor?.backdrop) this.floor.backdrop.visible = origBackdropVis;
        if (this.floor?.shadowPlane) this.floor.shadowPlane.visible = origShadowVis;
        this.renderer.setClearColor(origClearColor, origClearAlpha);

        this.camera.position.copy(origPos);
        if (this.controls) {
          this.controls.target.copy(origTarget);
          this.controls.update();
        }
        this.camera.lookAt(origTarget);
      }
    }

    // Reactive parameter update methods
    updateWoodType(speciesKey) {
      if (!MaterialsEngine.WOOD_SPECIES[speciesKey]) return;
      this.options.woodType = speciesKey;
      if (this.currentSVG) this.loadSVG(this.currentSVG);
    }

    updateThickness(thicknessMm) {
      this.options.thickness = parseFloat(thicknessMm) || 5.0;
      if (this.currentSVG) this.loadSVG(this.currentSVG);
    }

    updateLayer2(layer2Config) {
      this.options.layer2 = Object.assign(this.options.layer2 || {}, layer2Config);
      if (this.currentSVG) this.loadSVG(this.currentSVG);
    }

    updateLighting(lightingKey) {
      this.options.lighting = lightingKey;
      SceneEngine.updateLighting(this.scene, this.lights, lightingKey);
    }

    updateAccessory(accessoryKey) {
      this.options.accessory = accessoryKey;
      if (this.currentSVG) this.loadSVG(this.currentSVG);
    }

    animate() {
      if (!this.isAnimating) return;
      this.animationFrameId = requestAnimationFrame(this.animate);

      // Smooth camera interpolation
      if (this.lerpingCamera && this.targetCameraPos) {
        this.camera.position.lerp(this.targetCameraPos, 0.08);
        this.controls.target.lerp(this.targetCameraTarget, 0.08);

        if (this.camera.position.distanceTo(this.targetCameraPos) < 1.0) {
          this.camera.position.copy(this.targetCameraPos);
          this.controls.target.copy(this.targetCameraTarget);
          this.lerpingCamera = false;
        }
      }

      if (this.controls) this.controls.update();
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    }

    destroy() {
      this.isAnimating = false;
      if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.handleResize);

      if (this.meshGroup) {
        while (this.meshGroup.children.length > 0) {
          const obj = this.meshGroup.children[0];
          SceneEngine.disposeObject(obj);
          this.meshGroup.remove(obj);
        }
      }
      if (this.renderer) {
        this.renderer.dispose();
        if (this.renderer.domElement && this.renderer.domElement.parentNode) {
          this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
      }
    }
  }

  // Namespace registrations
  window.Wood3D = {
    Viewer: Wood3DViewer,
    WOOD_SPECIES: MaterialsEngine.WOOD_SPECIES,
    FILLER_COLORS: MaterialsEngine.FILLER_COLORS,
    LIGHTING_PRESETS: SceneEngine.LIGHTING_PRESETS
  };

  window.LaserStudio.Wood3D = window.Wood3D;
})(window);


/* ==========================================================================
   3D MODULE: laser-studio-3d-snapshotter.js
   ========================================================================== */

/**
 * Laser Ornament Studio - 3D Template Thumbnail Snapshotter Engine
 * Module: assets/laser-studio-3d-snapshotter.js
 * Responsibility: Generates photorealistic 3D miniature snapshots for template cards on first page load and updates on wood change.
 */

window.LaserStudio = window.LaserStudio || {};

(function (window) {
  'use strict';

  const MaterialsEngine = window.LaserStudio.Wood3DMaterials;

  class Template3DRenderer {
    constructor() {
      this.cache = new Map(); // key: `${templateId}_${woodSpecies}` -> dataUrl
      this.isInitialized = false;
      this.isProcessing = false;
      this.renderQueue = [];
      this.activeSpecies = (window.LaserState && window.LaserState.selectedWoodSpecies) || 'birch';
      this.canvas = null;
      this.renderer = null;
      this.scene = null;
      this.camera = null;
    }

    /**
     * Initializes offscreen Three.js WebGL pipeline
     */
    initPipeline() {
      if (this.renderer) return true;
      if (typeof THREE === 'undefined' || !THREE.SVGLoader) {
        console.warn('[Template3D] Three.js or SVGLoader not loaded yet.');
        return false;
      }

      try {
        this.canvas = document.createElement('canvas');
        this.canvas.width = 180;
        this.canvas.height = 180;

        this.renderer = new THREE.WebGLRenderer({
          canvas: this.canvas,
          antialias: true,
          alpha: true,
          preserveDrawingBuffer: true,
          powerPreference: 'high-performance'
        });

        this.renderer.setSize(180, 180);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.0;
        if (THREE.sRGBEncoding) this.renderer.outputEncoding = THREE.sRGBEncoding;

        this.scene = new THREE.Scene();

        const ambient = new THREE.AmbientLight(0xffeedd, 0.65);
        this.scene.add(ambient);

        const keyLight = new THREE.DirectionalLight(0xffffff, 0.95);
        keyLight.position.set(25, 45, 55);
        this.scene.add(keyLight);

        const rimLight = new THREE.DirectionalLight(0xfff0dd, 0.4);
        rimLight.position.set(-30, -20, 35);
        this.scene.add(rimLight);

        this.camera = new THREE.PerspectiveCamera(36, 1, 1, 1000);
        return true;
      } catch (err) {
        console.warn('[Template3D] WebGL initialization error:', err);
        return false;
      }
    }

    /**
     * Unites multi-path template SVG into a single CompoundPath SVG with evenodd holes
     */
    getCompoundSvg(templateObj) {
      const rawSvg = templateObj.rawSvg || templateObj.iconSvg;
      if (!rawSvg) return null;

      if (typeof paper === 'undefined' || !paper.project) return rawSvg;

      try {
        const imported = paper.project.importSVG(rawSvg, { expandShapes: true, insert: false });
        if (!imported) return rawSvg;

        const pathsToWeld = [];
        function collect(item) {
          if (item instanceof paper.Path || item instanceof paper.CompoundPath) {
            pathsToWeld.push(item);
          } else if (item instanceof paper.Group && item.children) {
            item.children.forEach(collect);
          }
        }
        collect(imported);

        if (pathsToWeld.length === 0) {
          imported.remove();
          return rawSvg;
        }

        const compound = new paper.CompoundPath();
        compound.fillRule = 'evenodd';
        pathsToWeld.forEach((p) => {
          const clone = p.clone({ insert: false });
          clone.fillColor = 'black';
          clone.strokeColor = null;
          compound.addChild(clone);
        });

        const exportedSvg = compound.exportSVG({ asString: true });
        compound.remove();
        imported.remove();
        return exportedSvg;
      } catch (e) {
        console.warn('[Template3D] Compound SVG extraction fallback:', e);
        return rawSvg;
      }
    }

    /**
     * Renders 1 single frame snapshot of template with specific wood species
     */
    renderSnapshot(templateId, speciesKey) {
      const cacheKey = `${templateId}_${speciesKey}`;
      if (this.cache.has(cacheKey)) {
        return this.cache.get(cacheKey);
      }

      if (!this.initPipeline()) return null;

      const template =
        (window.LaserStudio && window.LaserStudio.TemplateRegistry)
          ? window.LaserStudio.TemplateRegistry.get(templateId)
          : (window.LaserTemplates ? window.LaserTemplates[templateId] : null);

      if (!template) return null;

      const compoundSvgString = this.getCompoundSvg(template);
      if (!compoundSvgString) return null;

      try {
        const loader = new THREE.SVGLoader();
        const svgData = loader.parse(compoundSvgString);
        if (!svgData || !svgData.paths || svgData.paths.length === 0) return null;

        const cutShapes = [];
        svgData.paths.forEach((path) => {
          const shapes = THREE.SVGLoader.createShapes(path);
          if (shapes && shapes.length > 0) cutShapes.push(...shapes);
        });

        if (cutShapes.length === 0) return null;

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        cutShapes.forEach((shape) => {
          shape.getPoints().forEach((p) => {
            minX = Math.min(minX, p.x);
            minY = Math.min(minY, p.y);
            maxX = Math.max(maxX, p.x);
            maxY = Math.max(maxY, p.y);
          });
        });

        const width = maxX - minX || 100;
        const height = maxY - minY || 100;
        const cx = (minX + maxX) / 2;
        const cy = (minY + maxY) / 2;

        const species = (MaterialsEngine && MaterialsEngine.WOOD_SPECIES)
          ? (MaterialsEngine.WOOD_SPECIES[speciesKey] || MaterialsEngine.WOOD_SPECIES.birch)
          : { roughness: 0.42, metalness: 0.03 };

        const bounds = { minX, minY, width, height, cx, cy };
        const textures = MaterialsEngine
          ? MaterialsEngine.createWoodTextures(speciesKey, [], bounds)
          : null;

        if (!textures) return null;

        const customUVGenerator = {
          generateTopUV: function (geometry, vertices, indexA, indexB, indexC) {
            const ax = vertices[indexA * 3], ay = vertices[indexA * 3 + 1];
            const bx = vertices[indexB * 3], by = vertices[indexB * 3 + 1];
            const cx = vertices[indexC * 3], cy = vertices[indexC * 3 + 1];
            return [
              new THREE.Vector2((ax - minX) / width, 1.0 - (ay - minY) / height),
              new THREE.Vector2((bx - minX) / width, 1.0 - (by - minY) / height),
              new THREE.Vector2((cx - minX) / width, 1.0 - (cy - minY) / height)
            ];
          },
          generateSideWallUV: function () {
            return [
              new THREE.Vector2(0, 0),
              new THREE.Vector2(1, 0),
              new THREE.Vector2(1, 1),
              new THREE.Vector2(0, 1)
            ];
          }
        };

        const extrudeSettings = {
          depth: 4.5,
          bevelEnabled: true,
          bevelSegments: 2,
          steps: 1,
          bevelSize: 0.6,
          bevelThickness: 0.6,
          UVGenerator: customUVGenerator
        };

        const geometry = new THREE.ExtrudeGeometry(cutShapes, extrudeSettings);
        geometry.translate(-cx, -cy, -2.25);
        geometry.computeVertexNormals();

        const faceMaterial = new THREE.MeshStandardMaterial({
          map: textures.faceMap,
          bumpMap: textures.faceBump,
          bumpScale: 1.2,
          roughness: species.roughness || 0.42,
          metalness: species.metalness || 0.03,
          side: THREE.FrontSide
        });

        const edgeMaterial = new THREE.MeshStandardMaterial({
          map: textures.edgeMap,
          roughness: 0.9,
          metalness: 0.02,
          side: THREE.FrontSide
        });

        const mesh = new THREE.Mesh(geometry, [faceMaterial, edgeMaterial]);
        mesh.rotation.x = Math.PI;
        mesh.rotation.y = THREE.MathUtils.degToRad(-8);
        mesh.rotation.x = Math.PI + THREE.MathUtils.degToRad(8);

        this.scene.add(mesh);

        const maxDim = Math.max(width, height);
        const fov = this.camera.fov * (Math.PI / 180);
        const cameraDist = (maxDim / 2) / Math.tan(fov / 2) * 1.18;
        this.camera.position.set(0, 0, cameraDist);
        this.camera.lookAt(0, 0, 0);

        this.renderer.render(this.scene, this.camera);
        const dataUrl = this.renderer.domElement.toDataURL('image/webp');

        this.scene.remove(mesh);
        geometry.dispose();
        faceMaterial.dispose();
        edgeMaterial.dispose();
        if (textures.faceMap) textures.faceMap.dispose();
        if (textures.faceBump) textures.faceBump.dispose();
        if (textures.faceRoughness) textures.faceRoughness.dispose();
        if (textures.edgeMap) textures.edgeMap.dispose();

        this.cache.set(cacheKey, dataUrl);
        return dataUrl;
      } catch (err) {
        console.warn('[Template3D] Error rendering template snapshot for', templateId, err);
        return null;
      }
    }

    applySnapshotToCard(card, dataUrl) {
      if (!card || !dataUrl) return;
      const iconContainer = card.querySelector('.template-card-icon');
      if (!iconContainer) return;

      let thumbImg = iconContainer.querySelector('.template-card-3d-thumb');
      if (!thumbImg) {
        thumbImg = document.createElement('img');
        thumbImg.className = 'template-card-3d-thumb';
        thumbImg.alt = card.querySelector('.template-card-title')?.textContent || 'Template Preview';
        iconContainer.appendChild(thumbImg);
      }

      thumbImg.onload = () => {
        thumbImg.classList.add('loaded');
        card.classList.add('has-3d-thumb');
      };
      thumbImg.src = dataUrl;
    }

    processQueue() {
      if (this.isProcessing || this.renderQueue.length === 0) return;
      this.isProcessing = true;

      const nextItem = this.renderQueue.shift();
      if (!nextItem) {
        this.isProcessing = false;
        return;
      }

      try {
        const dataUrl = this.renderSnapshot(nextItem.templateId, nextItem.speciesKey);
        if (dataUrl && nextItem.card) {
          this.applySnapshotToCard(nextItem.card, dataUrl);
        }
      } catch (e) {
        console.warn('[Template3D] Queue processing error:', e);
      }

      setTimeout(() => {
        this.isProcessing = false;
        this.processQueue();
      }, 25);
    }

    renderAllCards(speciesKey) {
      if (speciesKey) {
        this.activeSpecies = speciesKey;
      } else {
        speciesKey = this.activeSpecies;
      }

      const cards = document.querySelectorAll('#templatePicker .template-card[data-template]');
      if (!cards || cards.length === 0) return;

      cards.forEach((card) => {
        const templateId = card.getAttribute('data-template');
        if (!templateId) return;

        const cacheKey = `${templateId}_${speciesKey}`;
        if (this.cache.has(cacheKey)) {
          this.applySnapshotToCard(card, this.cache.get(cacheKey));
        } else {
          this.renderQueue.push({ card, templateId, speciesKey });
        }
      });

      this.processQueue();
    }

    updateWoodType(newSpecies) {
      if (!newSpecies || newSpecies === this.activeSpecies) return;
      this.activeSpecies = newSpecies;
      this.renderAllCards(newSpecies);
    }

    init() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      setTimeout(() => {
        this.renderAllCards(this.activeSpecies);
      }, 120);
    }
  }

  // Singleton registration
  window.Template3DRenderer = new Template3DRenderer();
  window.LaserStudio.ThumbnailRenderer = window.Template3DRenderer;
})(window);

